import {useMemo} from 'react';

import {useSortState} from './useSortState';
import type {SortDir} from './useSortState';

export type {SortDir};

/**
 * Client-side column sort for a fully-loaded list (the co-teacher roster, which is small and not
 * paginated). Sorting happens in memory rather than via a server refetch.
 *
 * `getValue` must be stable (define it at module scope) so the sort doesn't recompute every render.
 */
export const useClientSort = <Row>(
  rows: Row[],
  getValue: (row: Row, key: string) => string,
  initialKey: string,
  initialDir: SortDir = 'asc'
) => {
  const {sortKey, sortDir, toggleSort} = useSortState(initialKey, initialDir);

  const sorted = useMemo(() => {
    const next = [...rows].sort((a, b) =>
      getValue(a, sortKey).localeCompare(getValue(b, sortKey), undefined, {
        numeric: true,
        sensitivity: 'base'
      })
    );
    return sortDir === 'desc' ? next.reverse() : next;
  }, [rows, getValue, sortKey, sortDir]);

  return {sorted, sortKey, sortDir, toggleSort};
};
